/**
 * Scheduled publish — one queued publish of ONE price on ONE channel.
 *
 * Mirrors `PublishSchedule` in cxa-enricher-api
 * (`internal/enrichment/publish_schedule.go`, table
 * cxa.ENR_POP_PUBLISH_SCHEDULE). The API only queues the intent; a pipeline
 * CronJob fires the row and decides `active` vs `launched` at that moment, so
 * nothing here says which status a pending row will end up in.
 *
 * Everything is gated by the `enablePublishSchedule` flag on both sides.
 */

/**
 * Row status (`CK_ENR_POP_PUBLISH_SCHEDULE_STATUS`).
 *
 * Only `pending` can be cancelled. `running` is mid-flight; the rest are
 * history the drawer keeps showing, because "it was scheduled and then
 * cancelled" is exactly the question an operator asks next.
 */
export type PublishScheduleStatus =
  "pending" | "running" | "done" | "skipped" | "failed" | "cancelled";

/**
 * Which way the queued transition goes (`ACTION_TYPE`).
 *
 * `publish` moves a price designed → active|launched, `unpublish` takes it the
 * other way, active|launched → designed. One price may hold ONE open row of
 * each at the same time — on sale 1 Oct, off sale 31 Oct — so nothing here
 * treats "has a schedule" as a single boolean.
 */
export type PublishScheduleAction = "publish" | "unpublish";

export interface PublishSchedule {
  scheduleId: number;
  /** Groups the rows one scheduling call created (one per price). */
  batchId: string;
  /** The offering the price belongs to (wire: `id`). */
  id: string;
  priceId: string;
  channel: string;
  /** RFC 3339 with an offset, e.g. "2026-10-01T09:00:00+07:00". */
  scheduledAt: string;
  /** Optional on the wire: rows queued before the backend grew ACTION_TYPE
   *  carry none, and {@link isTakeDown} reads those as publishes. */
  action?: PublishScheduleAction;
  status: PublishScheduleStatus;
  /** Where the price landed once the row fired — `active` or `launched`. */
  resultStatus?: string;
  /** Why a fired row published nothing (e.g. the price left `designed`). */
  skipReason?: string;
  lastError?: string;
  attemptCount: number;
  executedAt?: string;
  createdDate: string;
  createdBy: string;
  updatedDate: string;
  updatedBy: string;
}

/** A schedule still expected to fire — the only kind that blocks re-scheduling
 *  a price (the API answers 409) and the only kind worth cancelling. */
export const isOpenSchedule = (schedule: PublishSchedule): boolean =>
  schedule.status === "pending" || schedule.status === "running";

/** Only a pending row can be cancelled: once the CronJob has claimed it
 *  (`running`) the publish is already under way. */
export const isCancellableSchedule = (schedule: PublishSchedule): boolean =>
  schedule.status === "pending";

/** Whether this row takes a price OFF sale. A row with no `action` predates
 *  ACTION_TYPE and is a publish — the direction it actually ran in. */
export const isTakeDown = (schedule: PublishSchedule): boolean =>
  schedule.action === "unpublish";

/**
 * The open row for one price in ONE direction, or null.
 *
 * The direction is a parameter rather than an afterthought because a price is
 * allowed one open publish AND one open take-down simultaneously; asking "is
 * this price scheduled?" without saying which way would hide one of them and
 * block an operator from arming the other.
 */
export const openScheduleFor = (
  schedules: readonly PublishSchedule[],
  priceId: string,
  action: PublishScheduleAction,
): PublishSchedule | null =>
  schedules.find(
    (s) =>
      s.priceId === priceId &&
      isOpenSchedule(s) &&
      (isTakeDown(s) ? "unpublish" : "publish") === action,
  ) ?? null;
